import { DollarSign, RefreshCw } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

export const CurrencyToggle = ({ isDarkMode = false }) => {
  const { displayCurrency, setDisplayCurrency, exchangeRates, refreshRates, loading } = useCurrency();

  const formatRate = () => {
    if (!exchangeRates?.usd_to_lrd) return 'Loading...';
    return `1 USD = ${exchangeRates.usd_to_lrd.toFixed(2)} LRD`;
  };

  const formatLastUpdated = () => {
    if (!exchangeRates?.last_updated) return '';
    const date = new Date(exchangeRates.last_updated);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2">
              <DollarSign className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} />
              <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                <SelectTrigger 
                  className={`w-[130px] h-8 text-xs ${
                    isDarkMode 
                      ? 'bg-slate-700 border-slate-600 text-white' 
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                  data-testid="currency-toggle"
                >
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
                  <SelectItem value="original" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                      Original + Conv
                    </span>
                  </SelectItem>
                  <SelectItem value="USD" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      USD Only
                    </span>
                  </SelectItem>
                  <SelectItem value="LRD" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      LRD Only
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className={isDarkMode ? 'bg-slate-700 text-white border-slate-600' : ''}>
            <div className="text-xs">
              <p className="font-medium">{formatRate()}</p>
              {exchangeRates?.source && (
                <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>
                  Source: {exchangeRates.source} • {formatLastUpdated()}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className={`h-8 w-8 ${isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              onClick={refreshRates}
              disabled={loading}
              data-testid="refresh-rates-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className={isDarkMode ? 'bg-slate-700 text-white border-slate-600' : ''}>
            <p className="text-xs">Refresh exchange rate</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default CurrencyToggle;
